import { useEffect, useMemo, useState, RefObject, ReactElement } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import {
  FilterAvailableValues,
  JourneyFilterDefaultValueMode,
} from "../../../types/analytics";
import AnalyticsFilterInputs, {
  AnalyticsPageJourneyFilterRestriction,
} from "../../../types/analytics/AnalyticsFilterInputs";
import AdvancedFilters from "./AdvancedFilters";
import JourneyPicker from "./JourneyPicker";
import AnalyticsComparePropertyPicker from "./AnalyticsComparePropertyPicker";
import AnalyticsDatesControl, {
  AnalyticsDatesControlHandle,
} from "./AnalyticsDatesControl";
import { AnalyticsDateMode } from "../../../helpers/analytics/analyticsDateModeHelpers";
import AudienceFacetEditor from "./AudienceFacetEditor";
import {
  AudienceFacetType,
  audienceFacetLabel,
  formatAudienceFacetSummary,
  labelMapFromCheckboxTree,
} from "../../../helpers/analytics/audienceFacetHelpers";
import { sameDateRange } from "../../../helpers/analytics/analyticsFilterBarHelpers";
import {
  PointInTimeAvailability,
  clampPointInTimeDate,
  isPointInTimeSelectionIncomplete,
  latestSelectablePointInTimeDate,
} from "../../../helpers/analytics/pointInTimeFilterHelpers";
import AnalyticsPastDataControl from "./AnalyticsPastDataControl";
import { Sidebar, Switch, Tooltip } from "../../common";
import SplitButton from "../../common/SplitButton";
import { AnalyticsAdvancedFilterType } from "./AdvancedFilters";
import { ANALYTICS_CUSTOM_FIELDS_FACET } from "../../../config/analytics/analyticsFilterShipFlags";
import CheckboxTreeNodeDto from "../../../types/dtos/generic/CheckboxTreeNodeDto";
import FilterCheckboxTreeApiResponseDto from "../../../types/analytics/FilterCheckboxTreeApiResponseDto";
import locationApi from "../../../api/dashboard/locationApi";
import jobTitleApi from "../../../api/dashboard/jobTitleApi";
import appraisalLevelApi from "../../../api/dashboard/appraisalLevelApi";
import userGroupApi from "../../../api/admin/userGroupApi";
import {
  AnalyticsDateShortcut,
  AnalyticsSavedFilterDto,
} from "../../../types/analytics/AnalyticsSavedFilter";
import { useAnalyticsActivityWindowResolve } from "../../../hooks/useAnalyticsActivityWindowResolve";
import { IDateRange } from "../../../types/generic";

interface AnalyticsAdvancedFiltersPanelProps {
  onClose(): void;
  carryFiltersAcrossPages: boolean;
  onCarryFiltersAcrossPagesChange(enabled: boolean): void;
  isSkippingPageDefault: boolean;
  journeyFilterDefaultValueMode: JourneyFilterDefaultValueMode;
  journeyFilterRestriction: AnalyticsPageJourneyFilterRestriction;
  availableValues: FilterAvailableValues;
  /** Seed for the panel draft (discarded on close without Apply). */
  selectedValues: AnalyticsFilterInputs;
  datesControlRef: RefObject<AnalyticsDatesControlHandle>;
  dateMode: AnalyticsDateMode;
  onDateModeChange(mode: AnalyticsDateMode): void;
  stagedDateShortcut: AnalyticsDateShortcut;
  onExactDatesCompleteChange?(complete: boolean): void;
  onCommonDraftCompleteChange?(complete: boolean): void;
  exactDatesComplete?: boolean;
  commonDraftComplete?: boolean;
  dateChromeDraftIncomplete?: boolean;
  /** Data snapshot support for this page / client (ADR-0006). */
  pointInTimeAvailability: PointInTimeAvailability;
  /** Client history floor; the picker will not offer anything earlier. */
  pointInTimeEarliestSelectableDate: string | null;
  canUpdateApplied: boolean;
  appliedSavedItem: AnalyticsSavedFilterDto | null;
  onResetFilters(): void;
  /** Commit panel draft + date chrome, stage, and apply. False = stay open. */
  onApply(draft: AnalyticsFilterInputs): boolean;
  /** Persist the panel draft onto the applied saved filter. False = stay open. */
  onUpdate(draft: AnalyticsFilterInputs, onSuccess?: () => void): boolean;
  /** Open save-as-new using the panel draft (not the outer staged values). False = stay open. */
  onSaveAsNew(draft: AnalyticsFilterInputs): boolean;
}

function cloneFilterInputs(
  values: AnalyticsFilterInputs,
): AnalyticsFilterInputs {
  return {
    ...values,
    journeyKeys: values.journeyKeys ? [...values.journeyKeys] : values.journeyKeys,
    locationIds: values.locationIds ? [...values.locationIds] : values.locationIds,
    jobTitleIds: values.jobTitleIds ? [...values.jobTitleIds] : values.jobTitleIds,
    appraisalLevelIds: values.appraisalLevelIds
      ? [...values.appraisalLevelIds]
      : values.appraisalLevelIds,
    userGroupIds: [...(values.userGroupIds ?? [])],
    dateRange: values.dateRange
      ? {
          startDate: values.dateRange.startDate,
          endDate: values.dateRange.endDate,
        }
      : values.dateRange,
  };
}

/**
 * Overlay "All filters" panel for the analytics filter band.
 * Owns audience drill-in state and label catalogues for the drill footer.
 */
function AnalyticsAdvancedFiltersPanel({
  onClose,
  carryFiltersAcrossPages,
  onCarryFiltersAcrossPagesChange,
  isSkippingPageDefault,
  journeyFilterDefaultValueMode,
  journeyFilterRestriction,
  availableValues,
  selectedValues,
  datesControlRef,
  dateMode,
  onDateModeChange,
  stagedDateShortcut,
  onExactDatesCompleteChange,
  onCommonDraftCompleteChange,
  exactDatesComplete = true,
  commonDraftComplete = true,
  dateChromeDraftIncomplete = false,
  pointInTimeAvailability,
  pointInTimeEarliestSelectableDate,
  canUpdateApplied,
  appliedSavedItem,
  onResetFilters,
  onApply,
  onUpdate,
  onSaveAsNew,
}: AnalyticsAdvancedFiltersPanelProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const [draft, setDraft] = useState(() => cloneFilterInputs(selectedValues));
  /**
   * Data snapshot can be switched on before a date is picked; the draft only
   * gets a `pointInTimeDate` once there is one, so the toggle needs its own state.
   */
  const [pastDataOn, setPastDataOn] = useState(
    () => (selectedValues.pointInTimeDate?.trim() ?? "") !== "",
  );

  // Keep Data snapshot toggle/draft date in step when the parent clears or recalls PIT
  // (banner turn-off, saved-filter recall) without rewriting the whole draft on every render.
  useEffect(() => {
    const on = (selectedValues.pointInTimeDate?.trim() ?? "") !== "";
    setPastDataOn(on);
    setDraft((prev) => {
      const nextDate = selectedValues.pointInTimeDate ?? null;
      if ((prev.pointInTimeDate ?? null) === nextDate) {
        return prev;
      }
      return { ...prev, pointInTimeDate: nextDate };
    });
  }, [selectedValues.pointInTimeDate]);

  const [panelAudienceDrill, setPanelAudienceDrill] =
    useState<AudienceFacetType | null>(null);
  const [panelLocationTree, setPanelLocationTree] =
    useState<CheckboxTreeNodeDto[]>();
  const [panelJobTitleLabels, setPanelJobTitleLabels] =
    useState<Map<number, string>>();
  const [panelAppraisalLabels, setPanelAppraisalLabels] =
    useState<Map<number, string>>();
  const [panelUserGroupLabels, setPanelUserGroupLabels] =
    useState<Map<number, string>>();

  const availableJourneyKeys = useMemo(
    () =>
      availableValues.journeys.flatMap((g) => g.journeys.map((j) => j.key)),
    [availableValues.journeys],
  );

  const handlePanelFitRange = (range: IDateRange | null) => {
    setDraft((prev) => {
      if (sameDateRange(prev.dateRange, range)) return prev;
      return { ...prev, dateRange: range };
    });
  };

  // Resolve All activity against the panel draft journeys, not the outer band selection.
  const {
    emptyActivityHint,
    activityWindowError,
    retryActivityWindow,
  } = useAnalyticsActivityWindowResolve({
    stagedDateShortcut,
    allJourneys: draft.allJourneys,
    journeyKeys: draft.journeyKeys,
    availableJourneyKeys,
    onDateRangeChange: handlePanelFitRange,
  });

  const fitPreviewRangeLabel = useMemo(() => {
    if (!draft.dateRange?.startDate || !draft.dateRange?.endDate) {
      return "-";
    }
    return `${format(new Date(draft.dateRange.startDate), "d MMM yyyy")} - ${format(
      new Date(draft.dateRange.endDate),
      "d MMM yyyy",
    )}`;
  }, [draft.dateRange]);

  // Catalogues for panel drill-in footer summaries (names / compressed locations).
  useEffect(() => {
    if (!panelAudienceDrill) return;
    const token = auth.user?.access_token;
    if (panelAudienceDrill === "LOCATION" && panelLocationTree === undefined) {
      new locationApi(token).getLocationCheckboxTreeItemsByClientId(
        false,
        (data: FilterCheckboxTreeApiResponseDto) =>
          setPanelLocationTree(data.nodes),
        (error) => console.error(error),
      );
    }
    if (
      panelAudienceDrill === "JOB-TITLE" &&
      panelJobTitleLabels === undefined
    ) {
      new jobTitleApi(token).getJobTitleCheckboxTreeItemsByClientId(
        (data: FilterCheckboxTreeApiResponseDto) =>
          setPanelJobTitleLabels(labelMapFromCheckboxTree(data.nodes)),
        (error) => console.error(error),
      );
    }
    if (
      panelAudienceDrill === "APPRAISAL-LEVEL" &&
      panelAppraisalLabels === undefined
    ) {
      new appraisalLevelApi(token).getAppraisalLevelCheckboxTreeItemsByClientId(
        (data: FilterCheckboxTreeApiResponseDto) =>
          setPanelAppraisalLabels(labelMapFromCheckboxTree(data.nodes)),
        (error) => console.error(error),
      );
    }
    if (
      panelAudienceDrill === "USER-GROUP" &&
      panelUserGroupLabels === undefined
    ) {
      new userGroupApi(token).getAnalyticsUserGroups(
        (data) =>
          setPanelUserGroupLabels(
            new Map(data.map((g) => [g.id, g.name] as [number, string])),
          ),
        (error) => console.error(error),
      );
    }
  }, [
    panelAudienceDrill,
    panelLocationTree,
    panelJobTitleLabels,
    panelAppraisalLabels,
    panelUserGroupLabels,
    auth.user?.access_token,
  ]);

  const patchDraft = (partial: Partial<AnalyticsFilterInputs>) => {
    setDraft((prev) => ({ ...prev, ...partial }));
  };

  const pointInTimeDraftIncomplete = isPointInTimeSelectionIncomplete({
    availability: pointInTimeAvailability,
    isOn: pastDataOn,
    pointInTimeDate: draft.pointInTimeDate,
  });
  const commitBlocked = dateChromeDraftIncomplete || pointInTimeDraftIncomplete;

  const handlePastDataToggle = (isOn: boolean) => {
    setPastDataOn(isOn);
    if (!isOn) {
      patchDraft({ pointInTimeDate: null });
    }
  };

  const handlePastDataDateChange = (date: string | null) => {
    patchDraft({
      pointInTimeDate: clampPointInTimeDate(
        date,
        pointInTimeEarliestSelectableDate,
        latestSelectablePointInTimeDate(),
      ),
    });
  };

  const commitBlockedTitle =
    dateMode === "pick" && !exactDatesComplete
      ? t(
          "Analytics.Filters.ExactDatesRequired",
          "Choose both a from and to date.",
        )
      : dateMode === "common" && !commonDraftComplete
        ? t(
            "Analytics.Filters.CommonPresetRequired",
            "Choose a common date range.",
          )
        : pointInTimeDraftIncomplete
          ? t(
              "Analytics.Filters.PastDataDateRequired",
              "Choose a date before you apply.",
            )
          : undefined;

  const withCommitBlockedTooltip = (button: ReactElement) =>
    commitBlockedTitle ? (
      <Tooltip
        content={commitBlockedTitle}
        triggerElement={<span className="inline-flex">{button}</span>}
      />
    ) : (
      button
    );

  const onPanelFacetOpen = (filterType: AnalyticsAdvancedFilterType) => {
    setPanelAudienceDrill(filterType);
  };

  const selectedIdsForPanelFacet = (type: AudienceFacetType) => {
    switch (type) {
      case "APPRAISAL-LEVEL":
        return draft.appraisalLevelIds;
      case "LOCATION":
        return draft.locationIds;
      case "JOB-TITLE":
        return draft.jobTitleIds;
      case "USER-GROUP":
        return draft.userGroupIds;
    }
  };

  const onPanelFacetIdsChange = (type: AudienceFacetType, ids: number[]) => {
    switch (type) {
      case "APPRAISAL-LEVEL":
        patchDraft({ appraisalLevelIds: ids });
        break;
      case "LOCATION":
        patchDraft({ locationIds: ids });
        break;
      case "JOB-TITLE":
        patchDraft({ jobTitleIds: ids });
        break;
      case "USER-GROUP":
        patchDraft({ userGroupIds: ids });
        break;
    }
  };

  const panelDrillSummary = (type: AudienceFacetType) => {
    const labelById =
      type === "APPRAISAL-LEVEL"
        ? panelAppraisalLabels
        : type === "JOB-TITLE"
          ? panelJobTitleLabels
          : type === "USER-GROUP"
            ? panelUserGroupLabels
            : panelLocationTree
              ? labelMapFromCheckboxTree(panelLocationTree)
              : undefined;
    return formatAudienceFacetSummary({
      type,
      selectedIds: selectedIdsForPanelFacet(type),
      t,
      labelById,
      locationTree: type === "LOCATION" ? panelLocationTree : undefined,
    });
  };

  const panelHeader = (
    <div className="flex items-center gap-2 pb-3 border-b border-gray-200">
      {panelAudienceDrill ? (
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900"
          onClick={() => setPanelAudienceDrill(null)}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
          {t("Common.Back", "Back")}
        </button>
      ) : (
        <h2 className="text-base font-medium text-gray-900">
          {t("Analytics.Filters.AdvancedFiltersTitle", "Advanced Filters")}
        </h2>
      )}
      {panelAudienceDrill && (
        <h2 className="text-base font-bold text-gray-900 truncate">
          {audienceFacetLabel(panelAudienceDrill, t)}
        </h2>
      )}
    </div>
  );

  const panelFooter = (
    <div className="flex items-center gap-2 text-left">
      {panelAudienceDrill ? (
        <>
          <span className="text-xs text-gray-500 truncate">
            {panelDrillSummary(panelAudienceDrill)}
          </span>
          <span className="flex-1" />
          <button
            type="button"
            className="btn-primary !px-4 !py-1.5 text-sm rounded-md"
            onClick={() => setPanelAudienceDrill(null)}
          >
            {t("Common.Done", "Done")}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="text-sm text-gray-600 hover:text-gray-900"
            onClick={() => {
              onResetFilters();
              onClose();
            }}
          >
            {t(
              "Analytics.Filters.RevertToPageDefaults",
              "Revert to page defaults",
            )}
          </button>
          <span className="flex-1" />
          {canUpdateApplied &&
            withCommitBlockedTooltip(
              <button
                type="button"
                className="btn-secondary !px-3 !py-1.5 text-sm rounded-md"
                disabled={commitBlocked}
                onClick={() => {
                  onUpdate(draft, () => {
                    onClose();
                  });
                }}
              >
                {t(
                  "Analytics.Filters.SaveDialog.UpdateNamed",
                  "Update '{{name}}'",
                  { name: appliedSavedItem?.name ?? "" },
                )}
              </button>,
            )}
          {withCommitBlockedTooltip(
            <SplitButton
              wideMenuOnDesktop
              primaryAction={{
                label: t("Analytics.Filters.Apply", "Apply"),
                disabled: commitBlocked,
                onClick: () => {
                  if (onApply(draft)) {
                    onClose();
                  }
                },
              }}
              secondaryActions={[
                {
                  label: t(
                    "Analytics.Filters.SaveDialog.SaveAsNew",
                    "Save as a new filter…",
                  ),
                  disabled: commitBlocked,
                  onClick: () => {
                    if (onSaveAsNew(draft)) {
                      onClose();
                    }
                  },
                },
              ]}
            />,
          )}
        </>
      )}
    </div>
  );

  return (
    <Sidebar
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      allowOutsideClose
      title={null}
      headerComponent={panelHeader}
      width="SLIM"
      footerComponent={panelFooter}
    >
      <div className="py-2">
        {panelAudienceDrill ? (
            <div className="py-3">
              <AudienceFacetEditor
                key={panelAudienceDrill}
                facetType={panelAudienceDrill}
                selectedIds={selectedIdsForPanelFacet(panelAudienceDrill)}
                onSelectedIdsChange={(ids) =>
                  onPanelFacetIdsChange(panelAudienceDrill, ids)
                }
              />
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              <section className="py-5 first:pt-3">
                <div className="flex items-center justify-between gap-3">
                  <label
                    id="analytics-pin-filters-label"
                    className="text-sm font-semibold text-gray-900"
                  >
                    {t(
                      "Analytics.Filters.PinFiltersSwitch",
                      "Use these filters as I change pages",
                    )}
                  </label>
                  <Switch
                    checked={carryFiltersAcrossPages}
                    onChange={onCarryFiltersAcrossPagesChange}
                    ariaLabelledBy="analytics-pin-filters-label"
                    size="norm"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {t(
                    "Analytics.Filters.PinFiltersHelper",
                    "Pages with their own default filters will use the current filters instead.",
                  )}
                </p>
                {isSkippingPageDefault && (
                  <div className="mt-2 flex items-start justify-between gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2">
                    <p className="text-xs text-green-900">
                      {t(
                        "Analytics.Filters.SkippingPageDefaultNote",
                        "This page's default filters aren't applied.",
                      )}
                    </p>
                    <button
                      type="button"
                      className="shrink-0 text-xs font-semibold text-green-800 underline"
                      onClick={() => onCarryFiltersAcrossPagesChange(false)}
                    >
                      {t("Analytics.Filters.UnpinFilters", "Unpin")}
                    </button>
                  </div>
                )}
              </section>

              <section className="py-5">
                <h4 className="text-sm font-bold mb-2">
                  {t("Analytics.Filters.Journeys", "Journeys")}
                </h4>
                <JourneyPicker
                  defaultValueMode={journeyFilterDefaultValueMode}
                  restriction={journeyFilterRestriction}
                  items={availableValues.journeys}
                  onValueChange={(keys) =>
                    patchDraft({ allJourneys: false, journeyKeys: keys })
                  }
                  allSelected={draft.allJourneys}
                  selectedValues={draft.journeyKeys}
                  inEditMode={true}
                  onBeginEditMode={() => {}}
                  onAllSelectedChange={(allSelected) => {
                    patchDraft({
                      allJourneys: allSelected,
                      journeyKeys: allSelected ? availableJourneyKeys : [],
                    });
                  }}
                />
              </section>

              <section className="py-5">
                <h4 className="text-sm font-bold mb-2">
                  {t("Analytics.Filters.Dates", "Dates")}
                </h4>
                <AnalyticsDatesControl
                  ref={datesControlRef}
                  dateMode={dateMode}
                  onDateModeChange={onDateModeChange}
                  stagedDateShortcut={stagedDateShortcut}
                  dateRange={draft.dateRange}
                  fitPreviewLabel={fitPreviewRangeLabel}
                  emptyActivityHint={emptyActivityHint}
                  activityWindowError={activityWindowError}
                  onRetryActivityWindow={retryActivityWindow}
                  onExactDatesCompleteChange={onExactDatesCompleteChange}
                  onCommonDraftCompleteChange={onCommonDraftCompleteChange}
                />
              </section>

              <section className="py-5">
                <h4 className="text-sm font-bold mb-2">
                  {t("Analytics.Filters.Audience", "Audience")}
                </h4>
                <AdvancedFilters
                  selectedValues={draft}
                  inEditMode={true}
                  onBeginEditMode={() => {}}
                  onLocationPickerChange={(ids) =>
                    patchDraft({ locationIds: ids })
                  }
                  onAppraisalLevelPickerChange={(ids) =>
                    patchDraft({ appraisalLevelIds: ids })
                  }
                  onJobTitlePickerChange={(ids) =>
                    patchDraft({ jobTitleIds: ids })
                  }
                  onChangeApprovedDataOnly={(approvedOnly) =>
                    patchDraft({ includePartiallyApproved: !approvedOnly })
                  }
                  onChangeIncludeLeavers={(includeLeavers) =>
                    patchDraft({ activeUsersOnly: !includeLeavers })
                  }
                  onUserGroupPickerChange={(ids) =>
                    patchDraft({ userGroupIds: ids })
                  }
                  hideEmployeeStatus
                  onFacetOpen={onPanelFacetOpen}
                />
                {ANALYTICS_CUSTOM_FIELDS_FACET && (
                  <div className="mt-2 text-xs text-gray-400">
                    {t(
                      "Analytics.Filters.CustomFieldsStub",
                      "Custom fields - coming later",
                    )}
                  </div>
                )}
              </section>

              <section className="py-5">
                <h4 className="text-sm font-bold mb-2">
                  {t(
                    "Analytics.Filters.WhichEmployees",
                    "Which employees to include",
                  )}
                </h4>
                <div className="flex border border-gray-300 rounded-md overflow-hidden mb-2">
                  <button
                    type="button"
                    className={cx(
                      "flex-1 px-2 py-2 text-xs font-semibold border-r border-gray-300",
                      draft.activeUsersOnly
                        ? "bg-gray-200 text-gray-800"
                        : "bg-white text-gray-600 hover:bg-gray-50",
                    )}
                    onClick={() => patchDraft({ activeUsersOnly: true })}
                  >
                    {t("Analytics.Filters.ActiveOnly", "Active only")}
                  </button>
                  <button
                    type="button"
                    className={cx(
                      "flex-1 px-2 py-2 text-xs font-semibold",
                      !draft.activeUsersOnly
                        ? "bg-gray-200 text-gray-800"
                        : "bg-white text-gray-600 hover:bg-gray-50",
                    )}
                    onClick={() => patchDraft({ activeUsersOnly: false })}
                  >
                    {t(
                      "Analytics.Filters.ActivePlusLeavers",
                      "Active + leavers",
                    )}
                  </button>
                </div>
              </section>

              {journeyFilterDefaultValueMode === "MULTI" && (
                <section className="py-5">
                  <h4 className="text-sm font-bold mb-2">
                    {t("Analytics.Filters.Comparison", "Comparison")}
                  </h4>
                  <AnalyticsComparePropertyPicker
                    selectedValue={draft.comparisonProperty}
                    isEditMode={true}
                    onBeginEditMode={() => {}}
                    onSelectedValueChanged={(value) =>
                      patchDraft({ comparisonProperty: value })
                    }
                  />
                </section>
              )}

              <section className="py-5">
                <AnalyticsPastDataControl
                  availability={pointInTimeAvailability}
                  earliestSelectableDate={pointInTimeEarliestSelectableDate}
                  isOn={pastDataOn}
                  value={draft.pointInTimeDate ?? null}
                  onToggleChange={handlePastDataToggle}
                  onDateChange={handlePastDataDateChange}
                />
              </section>
            </div>
          )}
      </div>
    </Sidebar>
  );
}

export default AnalyticsAdvancedFiltersPanel;
